import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignUp() {
  const [form, setForm] = useState({ fullName: '', email: '', password: '', terms: false });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      navigate('/onboarding/step-1');
    } finally { setLoading(false); }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API_URL}/api/auth/google/login/`;
  };

  const handleGitHubLogin = () => {
    window.location.href = `${API_URL}/api/auth/github/login/`;
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 pt-20">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="text-emerald-500 font-mono font-bold text-xl inline-block mb-6">
            <span className="text-cyan-400">{'>'}</span>_ClawStinger
          </Link>
          <h1 data-testid="signup-title" className="text-2xl font-bold text-white font-mono mb-2">Create your account</h1>
          <p className="text-sm text-zinc-500">Start your 14-day free trial</p>
        </div>

        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-8">
          <div className="space-y-3 mb-6">
            <button data-testid="signup-google-btn" onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 border border-zinc-700 rounded-sm py-3 text-sm font-mono text-zinc-300 hover:bg-white/5 transition-all">
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </button>
            <button data-testid="signup-github-btn" onClick={handleGitHubLogin} className="w-full flex items-center justify-center gap-3 border border-zinc-700 rounded-sm py-3 text-sm font-mono text-zinc-300 hover:bg-white/5 transition-all">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg>
              Continue with GitHub
            </button>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-zinc-800" /><span className="text-xs text-zinc-600 font-mono">OR</span><div className="flex-1 h-px bg-zinc-800" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1.5">Full Name</label>
              <input data-testid="signup-name-input" type="text" required value={form.fullName} onChange={e => setForm({...form, fullName: e.target.value})} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 outline-none transition-all placeholder-zinc-600" placeholder="Alex Thompson" />
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1.5">Email</label>
              <input data-testid="signup-email-input" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 outline-none transition-all placeholder-zinc-600" placeholder="alex@company.com" />
            </div>
            <div className="relative">
              <label className="block text-xs font-mono text-zinc-500 mb-1.5">Password</label>
              <input data-testid="signup-password-input" type={showPw ? 'text' : 'password'} required value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50 outline-none transition-all pr-10 placeholder-zinc-600" placeholder="Min 8 characters" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-8 text-zinc-600 hover:text-zinc-400">{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input data-testid="signup-terms-checkbox" type="checkbox" checked={form.terms} onChange={e => setForm({...form, terms: e.target.checked})} className="w-4 h-4 rounded-sm bg-[#111] border-zinc-800 text-emerald-500 focus:ring-emerald-500/50" />
              <span className="text-xs text-zinc-500">I agree to the <Link to="/legal/terms" className="text-emerald-400 hover:underline">Terms</Link> and <Link to="/legal/privacy" className="text-emerald-400 hover:underline">Privacy Policy</Link></span>
            </label>
            <button data-testid="signup-submit-btn" type="submit" disabled={loading || !form.terms} className="w-full bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm py-3 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_15px_rgba(16,185,129,0.3)] disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-zinc-500 mt-6">
          Already have an account? <Link to="/signin" data-testid="signup-signin-link" className="text-emerald-400 hover:underline font-mono">Sign In</Link>
        </p>
      </div>
    </div>
  );
}
